import React from "react";
import Seo from "./Seo";

/**
 * About page - professional engineering bio with consistent design.
 */
function About() {
  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "LinuxSecOps",
    url: "https://www.linuxsecops.org",
    sameAs: [
      "https://github.com/xilpanda",
      "https://www.linkedin.com/in/sandro-radinkovic-18a8a35/",
    ],
  };

  return (
    <div className="about-container">
      <Seo
        title="About - LinuxSecOps"
        description="LinuxSecOps is a personal engineering platform focused on security infrastructure, DevSecOps automation, and practical security tools."
        path="/about"
        jsonLd={orgJsonLd}
      />

      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-content">
          <div className="hero-badge">About</div>
          <h1 className="about-hero-title">
            Security Engineering
            <span className="about-hero-accent"> & DevSecOps</span>
          </h1>
          <p className="about-hero-description">
            Personal engineering platform focused on Linux infrastructure security,
            DevSecOps automation, and practical security tools developed through real projects.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="about-content">
        {/* Background */}
        <section className="about-section">
          <div className="about-section-header">
            <div className="about-section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <h2 className="about-section-title">Background</h2>
          </div>
          <div className="about-section-content">
            <p>
              Senior DevOps and Security Engineer with 10+ years in enterprise and
              regulated environments. Experience spans infrastructure hardening,
              application security, detection engineering, and secure software development.
            </p>
            <p>
              Technical domains include CI/CD security, cloud posture management
              (AWS/Azure/GCP), container and Kubernetes security, SIEM integration,
              and threat detection content development.
            </p>
          </div>
        </section>

        {/* Work Areas */}
        <section className="about-section">
          <div className="about-section-header">
            <div className="about-section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <h2 className="about-section-title">Work Areas</h2>
          </div>
          <div className="about-cards-grid">
            <div className="about-card">
              <h3 className="about-card-title">DevSecOps Enablement</h3>
              <ul className="about-card-list">
                <li>CI/CD hardening, SBOM/SCA, secrets management</li>
                <li>Policy-as-code and compliance gates (NIST/ISO)</li>
                <li>Artifact signing and software supply chain security</li>
              </ul>
            </div>
            <div className="about-card">
              <h3 className="about-card-title">Security Engineering</h3>
              <ul className="about-card-list">
                <li>Application security (SAST/SCA/DAST integration)</li>
                <li>Cloud posture baselines and guardrails</li>
                <li>Detection engineering: MITRE mapping, Wazuh ruleset</li>
              </ul>
            </div>
            <div className="about-card">
              <h3 className="about-card-title">Software Development</h3>
              <ul className="about-card-list">
                <li>React/Next.js frontend, React Native mobile</li>
                <li>Python backend (FastAPI/Django)</li>
                <li>PostgreSQL, Redis, cloud infrastructure</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Tools & Projects */}
        <section className="about-section">
          <div className="about-section-header">
            <div className="about-section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                <polyline points="2 17 12 22 22 17"/>
                <polyline points="2 12 12 17 22 12"/>
              </svg>
            </div>
            <h2 className="about-section-title">Tools & Projects</h2>
          </div>
          <div className="about-projects-list">
            <a href="https://pentestscan.app" target="_blank" rel="noreferrer" className="about-project-item">
              <div className="about-project-info">
                <h4 className="about-project-name">PentestScan</h4>
                <p className="about-project-desc">Web security with attack paths and context - not just a finding list</p>
              </div>
              <span className="about-project-status status-live">Live</span>
            </a>
            <a href="https://testcasesystem.com" target="_blank" rel="noreferrer" className="about-project-item">
              <div className="about-project-info">
                <h4 className="about-project-name">TCMS</h4>
                <p className="about-project-desc">Self-hosted test case management system</p>
              </div>
              <span className="about-project-status status-live">Live</span>
            </a>
            <div className="about-project-item about-project-item-disabled">
              <div className="about-project-info">
                <h4 className="about-project-name">DarkExpose SOC</h4>
                <p className="about-project-desc">Threat intelligence platform with dark web and breach monitoring</p>
              </div>
              <span className="about-project-status status-dev">In Development</span>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="about-section">
          <div className="about-section-header">
            <div className="about-section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <h2 className="about-section-title">Process</h2>
          </div>
          <div className="about-process">
            <div className="process-step">
              <div className="process-step-number">1</div>
              <div className="process-step-content">
                <h4>Discovery</h4>
                <p>Gap analysis and requirements gathering</p>
              </div>
            </div>
            <div className="process-arrow">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>
            <div className="process-step">
              <div className="process-step-number">2</div>
              <div className="process-step-content">
                <h4>Baseline</h4>
                <p>Security posture assessment</p>
              </div>
            </div>
            <div className="process-arrow">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>
            <div className="process-step">
              <div className="process-step-number">3</div>
              <div className="process-step-content">
                <h4>Automation</h4>
                <p>Implementation and integration</p>
              </div>
            </div>
            <div className="process-arrow">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>
            <div className="process-step">
              <div className="process-step-number">4</div>
              <div className="process-step-content">
                <h4>Observability</h4>
                <p>Monitoring and continuous improvement</p>
              </div>
            </div>
          </div>
          <p className="about-process-note">
            Typical engagement: 2-4 week assessment, followed by implementation
            sprints with validation artifacts. Ongoing managed services available.
          </p>
        </section>

        {/* Contact CTA */}
        <section className="about-cta">
          <h2 className="about-cta-title">Ready to discuss your security needs?</h2>
          <p className="about-cta-description">
            For consulting inquiries, technical discussions, or collaboration opportunities.
          </p>
          <a href="mailto:linuxsecops@proton.me" className="btn-primary">
            Get in Touch
          </a>
        </section>
      </div>
    </div>
  );
}

export default About;
